import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames/dedupe';
import PropTypes from 'prop-types';
import React from 'react';
// @ts-expect-error
import PureRenderMixin from 'react-addons-pure-render-mixin';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {
  FOLDER_ITEM_TYPES,
  PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES,
} from 'constants/ModelTypeConstants';
import filestates from 'FileStates';
import {CaptionEditableItemContext} from 'notebook/mediocre/base-plugin/CaptionEditableItemContext';
import NoteDropTarget, {EnrichedFileUploadTask} from 'notebook/notes/drag-and-drop/NoteDropTarget';
import NoteConstants from 'notebook/notes/NoteConstants';
import PreviewResizer from 'notebook/notes/previews/PreviewResizer';
import UploadDropdownButton from 'notebook/notes/previews/UploadDropdownButton';
import {
  AttachedExternalFileAddedVia,
  trackAttachedExternalFile,
} from 'notebook/util/analytics/FileAnalyticsUtils';
import {NotePartItem, UploadSource} from 'notebook/util/NoteUtilConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {LinkedItem} from 'ui-library/attach-dropdown/AttachDropdownConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import {HTMLInputEvent} from 'util/Types';
import {FILE_TYPES_WITH_MODAL_EDITOR} from 'workflows/WorkflowConstants';

const ALLOWED_LINK_TYPES = NoteConstants.NOTE_PREVIEW_SUPPORTED_TYPES.concat(
  PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES
);
export const ONE_MEGABYTE = 1024 * 1024;
export const MAX_FILE_SIZE_FOR_PREVIEW_BYTES = 100 * ONE_MEGABYTE;

function _stopPropagationForMediocreItemSelect(e: Event): void {
  e.stopPropagation();
}

export interface PreviewProps {
  fileId: string;
  /** Stable note-part id used for deterministic Data Test Attributes in browser tests. */
  notePartCid?: string;
  followLink?: boolean;
  showOpenLink?: boolean;
  placeholder: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openAttachModal?: () => any;
  setItemWidth?: (itemWidth: number) => void;
  finalizeItemWidth: (index: number, itemWidth: number) => void;
  locked: boolean;
  editorWidth: number;
  alignment?: 'left' | 'center' | 'right' | null;
  openSequenceFeatureModal?: (e: React.MouseEvent) => void;
  // We expect the Preview to be wrapped by the IntermediateBinder, which partially applies the index of the
  // NotePart already
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadFile: (file: any, uploadSource?: UploadSource) => void;
  disableDropMessage?: string;
  handleDropExternalFiles: (fileUploadTasks: Array<EnrichedFileUploadTask>) => void;
  // We expect the Preview to be wrapped by the IntermediateBinder, which partially applies the index of the
  // NotePart already
  handleDropBenchlingFile: (item: LinkedItem) => void;
  item?: NotePartItem | null;
  isSelected: boolean;
}

const PreviewMixin: React.ComponentSpec<PreviewProps, {}> = {
  mixins: [PureRenderMixin],

  propTypes: {
    fileId: PropTypes.string.isRequired,
    followLink: PropTypes.bool.isRequired,
    showOpenLink: PropTypes.bool.isRequired,
    placeholder: PropTypes.bool.isRequired,
    openAttachModal: PropTypes.func,
    setItemWidth: PropTypes.func,
    finalizeItemWidth: PropTypes.func.isRequired,
    locked: PropTypes.bool,
    editorWidth: PropTypes.number,
    alignment: PropTypes.string,
    openSequenceFeatureModal: PropTypes.func,
    uploadFile: PropTypes.func.isRequired,
    disableDropMessage: PropTypes.string,
    handleDropExternalFiles: PropTypes.func.isRequired,
    handleDropBenchlingFile: PropTypes.func.isRequired,
    item: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      editURL: PropTypes.string,
      url: PropTypes.string,
      tagSchema: PropTypes.shape({
        iconId: PropTypes.string.isRequired,
      }),
    }),
    isSelected: PropTypes.bool.isRequired,
  },

  getDefaultProps(): PreviewProps {
    // @ts-expect-error
    return {
      showOpenLink: true, // allow opening / show link next to name
      followLink: false, // Open file in new tab on link click / image double click
    };
  },

  canOpen(): boolean {
    return (
      this.props.showOpenLink &&
      Array.from(ALLOWED_LINK_TYPES).includes(this.noteType) &&
      !this.props.placeholder &&
      !FILE_TYPES_WITH_MODAL_EDITOR.includes(APIUtil.apiIdToType(this.props.fileId))
    );
  },

  defaultRenderName(): JSX.Element {
    return (
      <div
        className="note-itemName"
        {...getTestProps({component: 'PreviewMixin', element: 'previewItemName'})}
      >
        {typeof this.renderName === 'function' ? this.renderName() : this.props.item.name}
        {typeof this.renderNameExtra === 'function' ? this.renderNameExtra(this.props.isSelected) : undefined}
      </div>
    );
  },

  defaultItemType(): string {
    return this.getItemType ? this.getItemType() : 'file';
  },

  itemURL(): string {
    if (Array.from(FOLDER_ITEM_TYPES).includes(this.noteType)) {
      return this.props.item.editURL;
    } else {
      return this.props.item.url;
    }
  },

  renderOpen(): Array<JSX.Element> | null {
    if (typeof this.isOpenDisabled === 'function' && this.isOpenDisabled()) {
      return null;
    }
    const fileSize = this.props.item.externalFile?.fileSize ?? 0; // Default to 0 if undefined
    const isFileSizeValid = fileSize <= MAX_FILE_SIZE_FOR_PREVIEW_BYTES;
    const tooltipText = isFileSizeValid
      ? `Open ${this.defaultItemType()}`
      : `File too large to open ${this.defaultItemType()}`;
    if (this.canOpen()) {
      return [
        <Tooltip key="open" text={tooltipText}>
          <Button
            key="open"
            className="previewCTA hidden-for-print"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.MINI}
            icon="arrow-up-right-and-arrow-down-left-from-center"
            onClick={this.defaultOpenItem}
            disabled={!isFileSizeValid}
          />
        </Tooltip>,
        ...(typeof this.renderOpenExtra === 'function' ? [this.renderOpenExtra()] : []),
      ];
    } else {
      return null;
    }
  },

  renderTypeIcon(): JSX.Element | undefined {
    if (typeof this.renderCustomTypeIcon === 'function') {
      return this.renderCustomTypeIcon();
    }
    if (this.noteType != null) {
      return (
        <BenchlingIcon
          icon={this.props.item?.tagSchema?.iconId ?? this.noteType}
          className="note-icon u-small-spacing-right pull-left"
        />
      );
    }
    return undefined;
  },

  defaultOpenItem(e: Event): void {
    _stopPropagationForMediocreItemSelect(e);
    e.preventDefault();
    if (typeof this.isOpenDisabled === 'function' && this.isOpenDisabled()) {
      return;
    }
    if (!this.canOpen()) {
      return;
    }
    if (this.props.followLink) {
      window.open(this.itemURL(), '_blank');
    } else if (this.openItem != null) {
      this.openItem(e);
    } else {
      filestates.get().openFolderItem(this.props.item.id, {open: true, source: 'note-preview'});
    }
  },

  fileChangeHandler(e: HTMLInputEvent): void {
    const file = e.target.files![0];
    if (file == null) {
      return;
    }
    this.props.uploadFile(file, UploadSource.LOCAL_FILE);
    trackAttachedExternalFile(
      this.props.fileId,
      AttachedExternalFileAddedVia.InsertAttachment,
      file.type,
      file.size,
      file.name,
      UploadSource.LOCAL_FILE
    );
  },

  /**
   * Helper method to redirect a click on the Upload a File button to trigger the file input's
   * onChange handler
   */
  _triggerFileInput(): void {
    this.fileInput.click();
  },

  _handleGoogleDriveFiles(files: Array<File>): void {
    if (files.length > 0) {
      this.props.uploadFile(files[0], UploadSource.GOOGLE_DRIVE);
      trackAttachedExternalFile(
        this.props.fileId,
        AttachedExternalFileAddedVia.InsertAttachment,
        files[0].type,
        files[0].size,
        files[0].name,
        UploadSource.GOOGLE_DRIVE
      );
    }
  },

  renderSettings(): JSX.Element | null {
    if (typeof this.isOpenDisabled === 'function' && this.isOpenDisabled()) {
      return null;
    }
    if (!this.handleContextMenu) {
      return null;
    }
    return (
      <Tooltip key="more" text="More...">
        <Button
          className="previewCTA hidden-for-print"
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.MINI}
          onClick={this.handleContextMenu}
          icon="ellipsis"
        />
      </Tooltip>
    );
  },

  _setFileInputRef(input: HTMLInputElement): void {
    this.fileInput = input;
  },

  render(): JSX.Element {
    const hasImagePreviewFile = Boolean(
      typeof this.getImagePreviewFile === 'function' && this.getImagePreviewFile()
    );
    const extraClassName = typeof this.getItemClassName === 'function' ? this.getItemClassName() : null;
    const noteDropTargetTestAttrs = this.props.notePartCid
      ? {component: 'NoteDropTarget', element: 'noteLinkedFilePreview', key: this.props.notePartCid}
      : {component: 'NoteDropTarget', element: 'noteLinkedFilePreview'};
    return (
      <CaptionEditableItemContext.Consumer>
        {({isImageEditorOpen}) => (
          <NoteDropTarget
            multiple={false}
            fileId={this.props.fileId}
            locked={this.props.locked}
            disableDropMessage={this.props.disableDropMessage}
            handleDropExternalFiles={this.props.handleDropExternalFiles}
            handleDropBenchlingFile={this.props.handleDropBenchlingFile}
            hasExistingItem={Boolean(this.props.item)}
          >
            {/* When a preview is first created, it may not have a setItemWidth function (if it was attached with
              the modal, for instance). The resizer is not rendered until this is passed in. */}
            <div
              className={classNames(this.className, 'mediocre-itemSelect', 'note-item', extraClassName, {
                'note-item--placeholder': this.props.placeholder && this.props.task == null,
              })}
              onContextMenu={(e) => {
                if (typeof this.handleContextMenu === 'function') {
                  this.handleContextMenu(e);
                }
              }}
              onDoubleClick={(e) => {
                if (typeof this.isActivelyAnnotating === 'function' && this.isActivelyAnnotating()) {
                  return;
                }
                if (typeof this.isOpenDisabled === 'function' && this.isOpenDisabled()) {
                  return;
                }
                this.defaultOpenItem(e);
              }}
              {...getTestProps(noteDropTargetTestAttrs)}
            >
              {this.props.item != null ? (
                <div>
                  {this.props.setItemWidth != null &&
                  !this.props.locked &&
                  (!hasImagePreviewFile || !isImageEditorOpen) ? (
                    <PreviewResizer
                      alignment={this.props.alignment}
                      maxWidth={this.props.editorWidth}
                      setItemWidth={this.props.setItemWidth}
                      finalizeItemWidth={this.props.finalizeItemWidth}
                    />
                  ) : undefined}
                  <h5 className={css(styles.container)}>
                    <>
                      {typeof this.isImageToolbarOpen === 'function' && this.isImageToolbarOpen() ? (
                        <div className={css(styles.previewToolbarContainerEdit)}>
                          {typeof this.renderImageToolbar === 'function'
                            ? this.renderImageToolbar(this.props.isSelected)
                            : undefined}
                        </div>
                      ) : (
                        <div className={css(styles.previewToolbarContainer)}>
                          <div className={css(styles.previewNameContainer)}>
                            {this.renderTypeIcon()}
                            {this.defaultRenderName()}
                          </div>
                          <div className={css(styles.item)}>
                            {this.renderHeaderIcons && this.renderHeaderIcons()}
                          </div>
                          <div className={css(styles.item)}>{this.renderOpen()}</div>
                          <div className={css(styles.item)}>{this.renderSettings()}</div>
                        </div>
                      )}
                    </>
                  </h5>
                  {this.renderItem(this.props.isSelected)}
                </div>
              ) : this.props.placeholder ? (
                <div className="notePlaceholderItem">
                  {this.props.task != null ? (
                    <div className="note-preview-loading">
                      <CenteredHorizontalThrobber />
                      <div
                        className="u-small-spacing-top"
                        {...getTestProps({component: 'PreviewMixin', element: 'loadingIndicator'})}
                      >
                        Uploading file
                      </div>
                    </div>
                  ) : (
                    <div className="notePlaceholderItem-contentWrapper text-center">
                      <div className="notePlaceholderItem-text u-small-spacing-bottom">
                        Drag and drop a file from the sidebar to attach or
                      </div>
                      <input
                        key="hiddenFileInput"
                        type="file"
                        multiple={false}
                        className="hidden"
                        ref={this._setFileInputRef}
                        onChange={this.fileChangeHandler}
                        disabled={this.props.locked}
                        {...getTestProps({component: 'PreviewMixin', element: 'hiddenFileInput'})}
                      />
                      <div className="notePlaceholderItem-buttonWrapper">
                        {BenchlingConfig.ELN_ENABLE_GOOGLE_DRIVE_FILE_PICKER ? (
                          <UploadDropdownButton
                            onLocalUploadClick={this._triggerFileInput}
                            onGoogleDriveFilesReady={this._handleGoogleDriveFiles}
                            fileId={this.props.fileId}
                            multiselect={false}
                            disabled={this.props.locked}
                          />
                        ) : (
                          <Button
                            buttonStyle={ButtonStyle.SECONDARY}
                            size={ButtonSize.SMALL}
                            fullWidth={true}
                            key="uploadFileButton"
                            className="u-tiny-spacing-bottom"
                            onClick={this._triggerFileInput}
                            disabled={this.props.locked}
                            text="Upload a File"
                            dataTestAttrs={{component: 'PreviewMixin', element: 'uploadButton'}}
                          />
                        )}
                        {APIUtil.apiIdToType(this.props.fileId) === ModelTypeConstants.SEQUENCE ? (
                          <Button
                            buttonStyle={ButtonStyle.TERTIARY_GRAY}
                            size={ButtonSize.SMALL}
                            fullWidth={true}
                            onClick={this.props.openSequenceFeatureModal}
                            disabled={this.props.locked}
                            text="Select A Feature"
                          />
                        ) : undefined}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <h5 className="u-no-spacing-top u-text-uppercase faded u-no-spacing-bottom">
                    {this.renderTypeIcon()}
                    (Deleted)
                  </h5>
                  <div className="note-preview-body">
                    {`The ${typeToName(this.noteType)} corresponding to this note no longer exists.`}
                  </div>
                </div>
              )}
            </div>
          </NoteDropTarget>
        )}
      </CaptionEditableItemContext.Consumer>
    );
  },
};

export const styles = StyleSheet.create({
  container: {
    marginTop: 0,
    marginBottom: 0,
    width: '100%',
    overflowX: 'auto',
  },
  item: {
    float: 'right',
    alignContent: 'center',
  },
  previewToolbarContainerEdit: {
    height: '32px',
    alignContent: 'center',
  },
  previewToolbarContainer: {
    height: '32px',
    alignContent: 'center',
    display: 'flex',
  },
  previewNameContainer: {
    height: '32px',
    alignContent: 'center',
    float: 'left',
    flex: 1,
  },
});

export default PreviewMixin;
